import { useUrlQuery } from "hooks/useUrlQuery";
import Admin2 from "layouts/Admin2";
import { Link, useLocation, useParams } from "react-router-dom";
import Profile from "./Profile";
import useFetchUser from "api/UserResource/useFetchUser";
import AdminPageLoader from "components/AdminPageLoader";
import { BiMessageDetail } from "react-icons/bi";
import { profileNavTabs } from "./utils";
import Security from "./Security";
import useAuthStore from "store/authStore";
import { Role } from "utils/server-constants/roles";
import useFetchPermissions from "api/RoleResource/useFetchPermissions";
import Permissions from "./Permissions";
import { UserModel } from "models/UserModel";
import { Fragment } from "react";
import useFetchSubjectGroups from "api/SubjectGroupResource/useFetchSubjectGroups";
import SubjectGroups from "./SubjectGroups";
import { Permission } from "utils/server-constants/permissions";
import ManageUserCourierPrivilege from "./ManageUserCourierPrivilege";
import UserLibrary from "./UserLibrary";

const User = () => {
	const { userId } = useParams();

	const location = useLocation();

	const searchParams = useUrlQuery();

	const activeTabName = searchParams.get("tab") || profileNavTabs.Profile;

	const { hasRoles, hasPermissions } = useAuthStore();

	const baseSectionUrl = `/portal/users/${userId}`;

	const { data: subjectGroupsData, isLoading: isLoadingSubjectGroups } =
		useFetchSubjectGroups({
			no_pagination: true,
		});

	const subjectGroups = subjectGroupsData?.data || [];

	const { data: allPermissions, isLoading: isLoadingPermissions } =
		useFetchPermissions();

	const { data: payload, isLoading } = useFetchUser(userId);

	const user = payload?.data?.data;

	/** @type {string} role */
	const role = user?.role_name?.[0] || "";

	/** @type {Array<string>} permissions */
	const permissions = user?.permission_names || [];

	console.log({ payload });

	const assignedSubjectGroups =
		user?.assignedSubjectGroups?.map((i) => i.id) || [];

	const userModel = new UserModel({
		id: user?.id,
		firstName: user?.first_name,
		lastName: user?.last_name,
		role,
		permissions,
		email: user?.email,
		mobileNumber: user?.mobile_number,
	});

	const userHasCourierPrivilege = userModel.hasAnyPermission([
		Permission.Courier,
	]);

	// const userHasCourierPrivilege = true;

	function canShowTab(tab) {
		if (tab === profileNavTabs.Security) {
			return hasRoles([Role.SuperAdmin, Role.Admin]);
		}
		if (tab === profileNavTabs.Courier) {
			return (
				hasRoles([Role.SuperAdmin, Role.Admin]) &&
				hasPermissions([Permission.AdminCourier])
			);
		}
		if (tab === profileNavTabs.Iam) {
			return (
				hasRoles([Role.SuperAdmin, Role.Admin]) &&
				userModel.hasAnyRole([
					Role.SuperAdmin,
					Role.Admin,
					Role.SeniorPublisher,
					Role.Publisher,
					Role.Institution,
					Role.Educator,
				])
			);
		}
		if (tab === profileNavTabs.SubjectGroups) {
			return (
				hasRoles([Role.SuperAdmin, Role.Admin, Role.Institution]) &&
				userModel.hasAnyRole([Role.Student])
			);
		}
		if (tab === profileNavTabs.Library) {
			return (
				hasRoles([Role.SuperAdmin]) ||
				(hasRoles([Role.Admin]) &&
					hasPermissions([Permission.ViewUserLibrary]))
			);
		}
		return true;
	}

	function canShowTabContents(tab) {
		return canShowTab(tab) && activeTabName === tab;
	}

	const tabSections = [
		{
			label: "Profile",
			tabName: profileNavTabs.Profile,
			show: true,
		},
		{
			label: "Security",
			tabName: profileNavTabs.Security,
			show: canShowTab(profileNavTabs.Security),
		},
		{
			label: "Roles & Permissions",
			tabName: profileNavTabs.Iam,
			show: canShowTab(profileNavTabs.Iam),
		},
		{
			label: "Courier privileges",
			tabName: profileNavTabs.Courier,
			show: canShowTab(profileNavTabs.Courier),
		},
		{
			label: "Subject groups",
			tabName: profileNavTabs.SubjectGroups,
			show: canShowTab(profileNavTabs.SubjectGroups),
		},
		{
			label: "Library",
			tabName: profileNavTabs.Library,
			show: canShowTab(profileNavTabs.Library),
		},
	];

	if (isLoading || isLoadingPermissions || isLoadingSubjectGroups) {
		return <AdminPageLoader />;
	}

	if (!user) {
		return (
			<Admin2 fullWidth>
				<div className="flex flex-col items-center w-full justify-center flex-1 gap-3">
					<BiMessageDetail
						size={100}
						className=" text-gray-300 "
					/>

					<h3>User not found</h3>

					<Link
						to="/users/admin/all"
						className="text-blue-700"
					>
						Back to users
					</Link>
				</div>
			</Admin2>
		);
	}

	return (
		<Admin2
			fullWidth
			breadCrumbs={[
				{
					name: "Users",
					path: "/users/admin/all",
				},
				{
					name:
						"Account (" +
						tabSections?.find((section) => section.tabName === activeTabName)
							?.label +
						")",
					path: location.pathname,
				},
			]}
		>
			<div className="portal-page-container">
				<div className="space-y-1">
					<h3 className="font-bold flex items-center justify-between gap-2 max-w-max">
						<span>
							{user?.first_name} {user?.last_name}
						</span>

						<span className="text-white font-thin text-sm bg-akgreen px-2 py-1 rounded-full">
							{role}
						</span>
					</h3>
					<p>Manage profile and account settings</p>
				</div>

				<div className="border-t w-full" />

				<div className="portal-account-layout">
					<div className="portal-account-tabs">
						{tabSections
							?.filter((section) => section.show)
							?.map((section, index) => (
								<Link
									key={index}
									aria-current={activeTabName === section.tabName ? "page" : undefined}
                                    to={`${baseSectionUrl}?tab=${section.tabName}`}
									className={`py-2 px-4 rounded-xl ${
										activeTabName === section?.tabName ? "bg-gray-100" : ""
									}`}
								>
									{section.label}
								</Link>
							))}
					</div>

					<div className="portal-settings-card min-w-0">
						<div
							className={`flex-1 flex ${activeTabName === profileNavTabs.Library ? "w-full" : "max-w-max"}`}
							style={{
								minWidth: 0,
							}}
						>
							{[
								{
									condition: canShowTabContents(profileNavTabs.Profile),
									component: (
										<Profile
											id={user?.id}
											firstName={user?.first_name}
											lastName={user?.last_name}
											email={user?.email}
											mobileNumber={user?.mobile_number}
											role={role}
										/>
									),
								},
								{
									condition: canShowTabContents(profileNavTabs.Security),
									component: <Security id={user?.id} />,
								},
								{
									condition: canShowTabContents(profileNavTabs.Courier),
									component: (
										<ManageUserCourierPrivilege
											id={user?.id}
											hasCourierPrivilege={userHasCourierPrivilege}
											permissions={permissions || []}
										/>
									),
								},
								{
									condition: canShowTabContents(profileNavTabs.Iam),
									component: (
										<Permissions
											id={user?.id}
											permissions={permissions || []}
											allPermissions={allPermissions?.data || []}
										/>
									),
								},
								{
									condition: canShowTabContents(profileNavTabs.SubjectGroups),
									component: (
										<SubjectGroups
											id={user?.id}
											assignedSubjectGroups={assignedSubjectGroups}
											subjectGroups={subjectGroups}
										/>
									),
								},
								{
									condition: canShowTabContents(profileNavTabs.Library),
									component: (
										<UserLibrary userId={user?.id} />
									),
								},
							].map(
								({ condition, component }, i) =>
									condition && <Fragment key={i}>{component}</Fragment>
							)}
						</div>
					</div>
				</div>
			</div>
		</Admin2>
	);
};

export default User;
